/**
 * Nicely formats a proportion as a percentage.
 *
 * A proportion is in the range [0, 1], while a percentage is in the range [0,
 * 100]. In the typical case, this function multiplies the proportion by 100
 * and rounds to the desired number of decimal places.
 * It also handles the following edge cases:
 *
 * - Null proportions are formatted as "n/a".
 * - Suppose the number of decimal places is 1. Percentages smaller than 0.1 are
 *   formatted as "<0.1". The exception is 0, which is formatted as "0.0".
 *   Similar logic is applied to percentages greater than 99.9.
 *
 * @param {number} proportion Proportion value to be formatted.
 * @param {number} decimals Number of decimal places to round the percentage to.
 *
 * @return {String} The formatted percentage value.
 */
export function formatAsPercentage (proportion, decimals) {
  if (proportion === null) {
    return 'n/a'
  }
  const pct = 100 * proportion
  if (pct === 0 || pct === 100) {
    return `${pct.toFixed(decimals)}`
  }
  const minValue = Math.pow(10, -decimals)
  const maxValue = 100 - minValue
  if (pct < minValue) {
    return `<${minValue}`
  }
  if (pct > maxValue) {
    return `>${maxValue}`
  }
  return `${pct.toFixed(decimals)}`
}

/**
 * Nicely formats a count rounded to the nearest thousand.
 *
 * In the typical case, this function rounds the count to the nearest thousand
 * and formats it using the user's locale. It also handles the following edge
 * cases:
 *
 * - Null counts are formatted as "n/a".
 * - Counts less than 1,000 are formatted as "<1,000". The exception is 0, which
 *   is formatted as "0".
 *
 * @param {number} count Count value to be formatted.
 *
 * @return {String} The formatted count.
 */
export function formatAsThousands (count) {
  if (count === null) {
    return 'n/a'
  }
  if (count === 0) {
    return '0'
  }
  if (count < 1000) {
    return `<${(1000).toLocaleString()}`
  }
  return (Math.round(count / 1000) * 1000).toLocaleString()
}

/**
 * Nicely formats a proportion.
 *
 * Examples:
 *   0.1234 => "12.3"
 *   0.0008 => "<0.1"
 *   0.0 => "0.0"
 *   null => "n/a"
 */
export function prettyPrintProportion (x) {
  return x === null ? 'n/a' : formatAsPercentage(x, 1)
}

/**
 * Nicely formats the confidence interval of a proportion.
 *
 * Examples:
 *   (0.1234, 0.2345) => "12.3 – 23.4"
 *   (0.0008, 0.2345) => "<0.1 – 23.4"
 *   (0.0, 0.2345) => "0.0 – 23.4"
 *   (null, null) => "n/a"
 */
export function prettyPrintProportionCI (x) {
  return x.lower === null
    ? 'n/a'
    : `${formatAsPercentage(x.lower, 1)} – ${formatAsPercentage(x.upper, 1)}`
}

/**
 * Nicely formats the sample size.
 *
 * Examples:
 *   55 => "55"
 *   1234 => "1,234"
 */
export function prettyPrintSampleSize (x) {
  return x === null ? 'n/a' : x.toLocaleString()
}

/**
 * Nicely formats the estimated population size.
 *
 * Examples:
 *   0.0 => "0"
 *   55.0 => "<1,000"
 *   1567.89 => "2,000"
 *   1234567.89 => "1,234,000"
 *   null => "n/a"
 */
export function prettyPrintPopulationSize (x) {
  return formatAsThousands(x)
}

/**
 * Nicely formats the confidence interval of an estimated population size.
 *
 * Examples:
 *   (890123.45, 1234567.89) => "890,100 – 1,234,000"
 *   (55.0, 1567.89) => "<1,000 - 2,000"
 *   (0.0, 1567.89) => "0 - 2,000"
 *   (null, null) => "n/a"
 */
export function prettyPrintPopulationSizeCI (x) {
  return x.lower === null
    ? 'n/a'
    : `${formatAsThousands(x.lower)} – ${formatAsThousands(x.upper)}`
}

export function safeStringForCSV (value) {
  return `"${csvSafeDoubleQuotes(value)}"`
}

export function csvSafeDoubleQuotes (value) {
  if (value.replace === undefined) {
    return value
  }

  return value.replace(/"/g, '""')
}
